import { getAddressBySymbol, TokenSymbolEnum } from '@/utils/tokens'
import {
  CMETH_UETH_PAIR_ADDRESS,
  HAHYPE_HYPE_PAIR_ADDRESS,
  HWHLP_USDHL_PAIR_ADDRESS,
  HWHLP_USDT0_PAIR_ADDRESS,
  LHYPE_USDT0_PAIR_ADDRESS,
  MHYPE_WHYPE_PAIR_ADDRESS,
  USDXL_LHYPE_PAIR_ADDRESS,
  WHLP_USDT0_PAIR_ADDRESS,
} from '../consts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Address } from 'viem'

type PointsConfig = {
  lend?: { multiplier: number }
  borrow?: { multiplier: number }
  deposit?: { multiplier: number }
  collateral?: { multiplier: number }
  title: string
  eligibleFor: string
  icon: string
  tooltipBody: string
}

type IsolatedPartnerPoints = {
  collateralSymbol: TokenSymbolEnum
  borrowSymbol: TokenSymbolEnum
  points: PointsConfig[]
}

const loopPoints = {
  deposit: { multiplier: 1 },
  title: 'LOOP Points',
  eligibleFor: 'LOOP Points.',
  icon: 'loopPoints',
  tooltipBody:
    'are eligible for LOOP points. 60% of LOOP token supply goes to holders via Early Adopter & Loyalty Rewards Program.',
}

const wavePoints = {
  lend: { multiplier: 3 },
  borrow: { multiplier: 3 },
  deposit: { multiplier: 1 },
  title: 'Wave Points',
  eligibleFor: 'Wave Points',
  icon: 'waves',
  tooltipBody: "are eligible for Hyperwave's Wave Points.",
}

export const isolatedPartnerPoints: Record<Address, IsolatedPartnerPoints> = {
  [MHYPE_WHYPE_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.MHYPE,
    borrowSymbol: TokenSymbolEnum.HYPE,
    points: [
      {
        lend: { multiplier: 2 },
        borrow: { multiplier: 1 },
        deposit: { multiplier: 2 },
        title: 'SpinUp Drops',
        eligibleFor: 'SpinUp Drops.',
        icon: 'spinUpDrops',
        tooltipBody:
          'are eligible for SpinUp Drops. 30% of initial HPP supply will be airdropped to SpinUp Drops holders.',
      },
    ],
  },
  [CMETH_UETH_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.CMETH,
    borrowSymbol: TokenSymbolEnum.UETH,
    points: [
      {
        deposit: { multiplier: 10 },
        title: 'Powder',
        eligibleFor: 'Powder.',
        icon: 'powder',
        tooltipBody: 'are eligible for Powder.',
      },
    ],
  },
  [USDXL_LHYPE_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.LHYPE,
    borrowSymbol: TokenSymbolEnum.USDXL,
    points: [loopPoints],
  },
  [LHYPE_USDT0_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.LHYPE,
    borrowSymbol: TokenSymbolEnum.USD_T0,
    points: [loopPoints],
  },
  [HWHLP_USDT0_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.HW_HLP,
    borrowSymbol: TokenSymbolEnum.USD_T0,
    points: [wavePoints],
  },
  [HWHLP_USDHL_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.HW_HLP,
    borrowSymbol: TokenSymbolEnum.USDHL,
    points: [wavePoints],
  },
  [WHLP_USDT0_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.WHLP,
    borrowSymbol: TokenSymbolEnum.USD_T0,
    points: [loopPoints],
  },
  [HAHYPE_HYPE_PAIR_ADDRESS]: {
    collateralSymbol: TokenSymbolEnum.HAHYPE,
    borrowSymbol: TokenSymbolEnum.HYPE,
    points: [
      {
        borrow: { multiplier: 2 },
        lend: { multiplier: 3 },
        title: 'Harmonix Points',
        eligibleFor: 'Harmonix Points.',
        icon: 'harmonixPoints',
        tooltipBody: 'are eligible for Harmonix Points.',
      },
    ],
  },
}

type PooledPartnerPoints = {
  assetSymbol: TokenSymbolEnum
  points: PointsConfig[]
}

export const pooledPartnerPoints: Record<Address, PooledPartnerPoints> = {
  [getAddressBySymbol(TokenSymbolEnum.KHYPE)]: {
    assetSymbol: TokenSymbolEnum.KHYPE,
    points: [
      {
        deposit: { multiplier: 0 },
        title: 'kPoints',
        eligibleFor: 'kPoints',
        icon: 'kPoints',
        tooltipBody: 'are eligible for kPoints.',
      },
    ],
  },
  [getAddressBySymbol(TokenSymbolEnum.THBILL)]: {
    assetSymbol: TokenSymbolEnum.THBILL,
    points: [
      {
        deposit: { multiplier: 0 },
        title: 'Theo points',
        eligibleFor: 'Theo points',
        icon: 'theoPoints',
        tooltipBody: 'are eligible for Theo points.',
      },
    ],
  },
  [getAddressBySymbol(TokenSymbolEnum.WST_HYPE)]: {
    assetSymbol: TokenSymbolEnum.WST_HYPE,
    points: [
      {
        deposit: { multiplier: 0 },
        title: 'Valantis Points',
        eligibleFor: 'Valantis Points',
        icon: 'valantisPoints',
        tooltipBody: 'are eligible for Valantis Points.',
      },
    ],
  },
}

// Collateral-based partner points for euler borrow markets
// Any euler market with matching collateral symbol will show these points
export const eulerPartnerPoints: Record<string, PointsConfig[]> = {
  [TokenSymbolEnum.LHYPE]: [
    {
      collateral: { multiplier: 0 },
      title: 'LOOP Points',
      eligibleFor: 'LOOP Points.',
      icon: 'loopPoints',
      tooltipBody:
        'are eligible for LOOP points. 60% of LOOP token supply goes to holders via Early Adopter & Loyalty Rewards Program.',
    },
  ],
  [TokenSymbolEnum.BBHLP]: [
    {
      collateral: { multiplier: 5 },
      title: 'Berries',
      eligibleFor: 'Berries.',
      icon: 'berriesPoints',
      tooltipBody: 'are eligible for Berries.',
    },
  ],
}

/**
 * Look up collateral-based partner points by asset symbol, mapping collateral → lend
 * so PointsBadge can render them in the lend/earn context.
 */
export function getCollateralPartnerPointsAsLend(assetSymbol: string | undefined): PointsConfig[] | undefined {
  if (!assetSymbol) return undefined
  const points = eulerPartnerPoints[assetSymbol]
  if (!points) return undefined
  const filtered = points.filter((p) => p.collateral)
  return filtered.length > 0 ? filtered.map((p) => ({ ...p, lend: p.collateral })) : undefined
}

export const incentivizedPairs = {}

export const POINTS_TOKEN_ADDRESS = CheckedAddress('0xf7a3e2dDC99e50d6510393Fa576e9293084f592B')
export const POINTS_CATCHALL_ADDRESS = CheckedAddress('0xA9DdD91249DFdd450E81E1c56Ab60E1A62651701')
export const POINTS_LEADERBOARD_BLACKLIST = [POINTS_CATCHALL_ADDRESS]
export const POINTS_LEADERBOARD_ITEMS = 1000

export const epochToTimestampMap: { epoch: number; timestamp: number; calculating?: boolean }[] = [
  { epoch: 29, timestamp: 1757030400 },
  { epoch: 28, timestamp: 1756339200 },
  { epoch: 27, timestamp: 1755734400 },
  { epoch: 26, timestamp: 1755129600 },
  { epoch: 25, timestamp: 1754524800 },
  { epoch: 24, timestamp: 1753920000 },
  { epoch: 23, timestamp: 1753315200 },
  { epoch: 22, timestamp: 1752710400 },
  { epoch: 21, timestamp: 1752105600 },
  { epoch: 20, timestamp: 1751500800 },
  { epoch: 19, timestamp: 1750896000 },
  { epoch: 18, timestamp: 1750291200 },
  { epoch: 17, timestamp: 1749686400 },
  { epoch: 16, timestamp: 1749081600 },
  { epoch: 15, timestamp: 1748476800 },
  { epoch: 14, timestamp: 1747872000 },
  { epoch: 13, timestamp: 1747267200 },
  { epoch: 12, timestamp: 1746662400 },
  { epoch: 11, timestamp: 1746057600 },
  { epoch: 10, timestamp: 1745452800 },
  { epoch: 9, timestamp: 1744848000 },
  { epoch: 8, timestamp: 1744243200 },
  { epoch: 7, timestamp: 1743638400 },
  { epoch: 6, timestamp: 1743033600 },
  { epoch: 5, timestamp: 1742428800 },
  { epoch: 4, timestamp: 1741824000 },
  { epoch: 3, timestamp: 1741219200 },
  { epoch: 2, timestamp: 1740614400 },
  { epoch: 1, timestamp: 1740268800 },
  { epoch: 0, timestamp: 1740009600 },
]
